import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  consumeRedirectResult,
  getFbAuth,
  isFirebaseConfigured,
  signInWithGoogle as fbSignInWithGoogle,
  signOut as fbSignOut,
  subscribeAuth,
  type FbUser,
} from './firebase';

interface AuthState {
  user: FbUser | null;
  loading: boolean;
  configured: boolean;
  /** Any error surfaced from the most recent redirect round-trip. Cleared
   *  on next successful sign-in or sign-out. Bubble this up in the UI so
   *  `auth/internal-error` doesn't fail silently. */
  redirectError: Error | null;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  getToken: () => Promise<string | null>;
}

const AuthCtx = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const configured = isFirebaseConfigured();
  const [user, setUser] = useState<FbUser | null>(null);
  const [loading, setLoading] = useState<boolean>(configured);
  const [redirectError, setRedirectError] = useState<Error | null>(null);

  useEffect(() => {
    if (!configured) {
      setLoading(false);
      return;
    }
    // Consume any redirect result FIRST so a failure on the previous
    // round-trip surfaces as an error here, not a silent
    // `auth/internal-error` swallowed by the SDK.
    void consumeRedirectResult().then(({ error }) => {
      if (error) {
        // Surface the actual FirebaseError fields. auth/internal-error alone
        // is uninformative; the cause lives in `code` or `customData`. Redact
        // `_tokenResponse` defensively — on some error paths it carries an
        // idToken or oauthAccessToken that we don't want in the console.
        const e = error as Error & {
          code?: string;
          customData?: { _tokenResponse?: unknown } & Record<string, unknown>;
          serverResponse?: { idToken?: unknown; refreshToken?: unknown } & Record<string, unknown>;
        };
        const redact = <T,>(o: T): T => {
          if (!o || typeof o !== 'object') return o;
          const out: Record<string, unknown> = { ...(o as Record<string, unknown>) };
          for (const k of ['_tokenResponse', 'idToken', 'refreshToken', 'oauthAccessToken', 'oauthIdToken']) {
            if (k in out) out[k] = '[redacted]';
          }
          return out as T;
        };
        console.error('[opensoi/auth] redirect result error', {
          message: e.message,
          code: e.code,
          customData: redact(e.customData),
          serverResponse: redact(e.serverResponse),
          stack: e.stack,
        });
        setRedirectError(error);
      }
    });
    const unsub = subscribeAuth((u) => {
      setUser(u);
      if (u) setRedirectError(null);
      setLoading(false);
    });
    return unsub;
  }, [configured]);

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      configured,
      redirectError,
      signInWithGoogle: async () => {
        if (!configured) throw new Error('Firebase not configured');
        setRedirectError(null);
        // Returns void in redirect mode — the user agent navigates away.
        await fbSignInWithGoogle();
      },
      signOut: async () => {
        if (!configured) return;
        setRedirectError(null);
        await fbSignOut();
      },
      getToken: async () => {
        if (!configured) return null;
        const u = getFbAuth().currentUser;
        if (!u) return null;
        return await u.getIdToken();
      },
    }),
    [user, loading, configured, redirectError],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
